import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FolderCheck, ExternalLink, UploadCloud, Archive, RotateCcw, Pencil,
  Send, Undo2, Trash2, MapPin, FolderInput, CheckCircle2, XCircle, Loader2,
  Video, Youtube, FileText, Link2,
} from "lucide-react";

/* The three content types. "youtube" (2026-09, Workstream A) is the only one
   with nothing stored anywhere: SalesOS keeps a validated 11-character video
   ID and builds the employee-facing embed URL server-side from it, so no
   Drive folder is needed and none of the Drive actions (Open in Drive,
   diagnostics, move-to-folder) apply to it. */
const CONTENT_TYPE_META = {
  document: { label: "Document", icon: FileText },
  video: { label: "Video", icon: Video },
  youtube: { label: "YouTube", icon: Youtube },
};

const STATUS_TONE = {
  draft: "bg-muted text-muted-foreground",
  published: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  archived: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]",
  deleted: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
};
const docStatusLabel = (d) => (d.deleted ? "deleted" : d.status || "draft");
const VISIBILITY_LABEL = {
  all: "All employees", departments: "Selected departments",
  roles: "Selected roles", leadership: "Leadership / Admin only",
};

/* 2026-09 — central Drive-backed company document library (SOPs,
   frameworks, policies, process manuals, sales scripts, training,
   compliance). Files physically live in Google Drive; SalesOS holds only
   metadata + a Drive reference (drive_file_id/drive_link) — never a
   binary blob in MongoDB, matching every existing Drive-backed feature.
   No vector/RAG ingestion here — every document keeps `ingested:false` so
   that can be added later without a schema change. */

const EMPTY_UPLOAD_FORM = {
  title: "", category: "", product_code: "", department: "", description: "",
};

const EMPTY_VISIBILITY = { type: "all", departments: [], roles: [] };

const EditDocDialog = ({ doc, categories, products, departmentOptions, roleOptions, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!doc) { setForm(null); return; }
    setForm({
      title: doc.title, category: doc.category, product_code: doc.product_code || "",
      department: doc.department || "", description: doc.description || "",
      visibility: { ...EMPTY_VISIBILITY, ...(doc.visibility || {}) },
      // Prefilled from the STORED video id, rebuilt into a canonical watch
      // URL — the original URL the admin pasted is deliberately never kept
      // (only the validated id is), so this shows the canonical form of what
      // the system actually holds.
      youtube_url: doc.youtube_video_id ? `https://www.youtube.com/watch?v=${doc.youtube_video_id}` : "",
    });
  }, [doc]);

  if (!doc || !form) return null;

  const toggleListValue = (key, value) => {
    setForm((f) => {
      const list = f.visibility[key] || [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...f, visibility: { ...f.visibility, [key]: next } };
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.warning("Title cannot be empty"); return; }
    if (form.visibility.type === "departments" && form.visibility.departments.length === 0) {
      toast.warning("Select at least one department"); return;
    }
    if (form.visibility.type === "roles" && form.visibility.roles.length === 0) {
      toast.warning("Select at least one role"); return;
    }
    if (doc.content_type === "youtube" && !form.youtube_url.trim()) {
      toast.warning("A YouTube link is required"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/knowledge/documents/${doc.document_id}`, {
        title: form.title.trim(), category: form.category,
        product_code: form.product_code || null, department: form.department || null,
        description: form.description, visibility: form.visibility,
        // Only sent for a YouTube item — the backend rejects youtube_url on
        // any other content type. Re-validated server-side by the same
        // extract_youtube_id() the create path uses, so an edit can't smuggle
        // in a URL that creation would have refused.
        ...(doc.content_type === "youtube" ? { youtube_url: form.youtube_url.trim() } : {}),
      });
      toast.success("Document updated");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">
          Edit {doc.content_type === "youtube" ? "YouTube video" : doc.content_type === "video" ? "video" : "document"}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input data-testid="knowledge-edit-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          {doc.content_type === "youtube" && (
            <div className="space-y-1">
              <Label className="text-xs">YouTube link</Label>
              <Input data-testid="knowledge-edit-youtube-url" className="text-xs" value={form.youtube_url}
                     onChange={(e) => setForm((f) => ({ ...f, youtube_url: e.target.value }))} />
              <p className="text-[10px] text-muted-foreground">
                Re-point this item at a different video. Validated server-side — only a single-video
                YouTube link is accepted.
              </p>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="knowledge-edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Product</Label>
              <Select value={form.product_code || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, product_code: v === "__none" ? "" : v }))}>
                <SelectTrigger data-testid="knowledge-edit-product"><SelectValue placeholder="Any product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Any product</SelectItem>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Department (what team this applies to)</Label>
            <Input data-testid="knowledge-edit-department" value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea data-testid="knowledge-edit-description" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="space-y-1.5 border-t pt-3">
            <Label className="text-xs font-semibold">Who can see this document</Label>
            <Select value={form.visibility.type} onValueChange={(v) => setForm((f) => ({ ...f, visibility: { ...f.visibility, type: v } }))}>
              <SelectTrigger data-testid="knowledge-edit-visibility-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(VISIBILITY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.visibility.type === "departments" && (
              <div className="flex flex-wrap gap-3 pt-1" data-testid="knowledge-edit-visibility-departments">
                {departmentOptions.length === 0 && <p className="text-xs text-muted-foreground">No employee departments found yet.</p>}
                {departmentOptions.map((d) => (
                  <label key={d} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={form.visibility.departments.includes(d)} onCheckedChange={() => toggleListValue("departments", d)} />
                    {d}
                  </label>
                ))}
              </div>
            )}
            {form.visibility.type === "roles" && (
              <div className="flex flex-wrap gap-3 pt-1" data-testid="knowledge-edit-visibility-roles">
                {roleOptions.map((r) => (
                  <label key={r} className="flex items-center gap-1.5 text-xs cursor-pointer capitalize">
                    <Checkbox checked={form.visibility.roles.includes(r)} onCheckedChange={() => toggleListValue("roles", r)} />
                    {r}
                  </label>
                ))}
              </div>
            )}
            {form.visibility.type === "leadership" && (
              <p className="text-[11px] text-muted-foreground">Visible only to Administration-capable staff and anyone in the Leadership department.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="knowledge-edit-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteDocDialog = ({ doc, onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setConfirmText(""); }, [doc]);

  if (!doc) return null;

  const del = async () => {
    setDeleting(true);
    try {
      await api.post(`/admin/knowledge/documents/${doc.document_id}/delete`, { confirm_title: confirmText });
      toast.success("Deleted — the Drive file was moved to Trash, not permanently removed");
      onDeleted();
    } catch (e) { toast.error(errDetail(e)); } finally { setDeleting(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-[hsl(var(--danger))]">Delete document</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p>This moves <strong>{doc.title}</strong>'s underlying Google Drive file to <strong>Trash</strong> (never a
            permanent delete — an admin can still recover it from Drive's own Trash) and removes it from active
            Knowledge Library management. It disappears from the employee Knowledge Base immediately.</p>
          <p className="text-xs text-muted-foreground">This is different from Archive — Archive keeps the file and only hides it from employees.</p>
          <div className="space-y-1">
            <Label className="text-xs">Type the document title to confirm: <span className="font-mono">{doc.title}</span></Label>
            <Input data-testid="knowledge-delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="knowledge-delete-confirm-submit" variant="destructive"
            disabled={deleting || confirmText !== doc.title} onClick={del}>
            {deleting ? "Deleting…" : "Delete permanently from SalesOS (Trash in Drive)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DiagnosticsDialog = ({ doc, onClose, onReconciled }) => {
  // `undefined` is the ONLY "not ready yet" sentinel here (loading, or no
  // doc open) — never `null` — so the render logic below only ever has to
  // check one falsy-but-not-yet-loaded case instead of two, which is what
  // previously let a stale `null` slip through to `diag.error` and crash.
  const [diag, setDiag] = useState(undefined);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    setDiag(undefined);
    if (!doc) return;
    api.get(`/admin/knowledge/documents/${doc.document_id}/diagnostics`)
      .then((r) => setDiag(r.data)).catch((e) => setDiag({ error: errDetail(e) }));
  }, [doc]);

  if (!doc) return null;

  const move = async () => {
    setMoving(true);
    try {
      const { data } = await api.post(`/admin/knowledge/documents/${doc.document_id}/move-to-knowledge-folder`);
      toast.success(data.status === "already_correct" ? "Already in the configured Knowledge folder" : "Moved into the configured Knowledge folder");
      const r = await api.get(`/admin/knowledge/documents/${doc.document_id}/diagnostics`);
      setDiag(r.data);
      onReconciled();
    } catch (e) { toast.error(errDetail(e)); } finally { setMoving(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Drive storage diagnostics</DialogTitle></DialogHeader>
        {diag === undefined ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking Google Drive…</p>
        ) : diag.error && !diag.drive_file_id ? (
          <p className="text-xs text-muted-foreground">{diag.error}</p>
        ) : (
          <div className="space-y-2 text-xs" data-testid="knowledge-diagnostics-body">
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
              <span className="text-muted-foreground">Drive file ID</span><span className="font-mono truncate">{diag.drive_file_id || "—"}</span>
              <span className="text-muted-foreground">Actual Drive name</span><span className="truncate">{diag.drive_name || "—"}</span>
              <span className="text-muted-foreground">MIME type</span><span className="truncate">{diag.drive_mime_type || "—"}</span>
              <span className="text-muted-foreground">Actual parent folder</span><span className="font-mono truncate">{(diag.drive_parents || []).join(", ") || "—"}</span>
              <span className="text-muted-foreground">Expected Knowledge folder</span><span className="font-mono truncate">{diag.expected_folder_id || "—"}</span>
              <span className="text-muted-foreground">Trashed in Drive</span><span>{diag.drive_trashed ? "Yes" : "No"}</span>
            </div>
            <div className="flex items-center gap-1.5 pt-1 border-t">
              {diag.location_verified === true ? (
                <><CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /><span className="font-medium">Location verified: YES</span></>
              ) : diag.location_verified === false ? (
                <><XCircle className="h-4 w-4 text-[hsl(var(--danger))]" /><span className="font-medium">Location verified: NO</span></>
              ) : (
                <span className="font-medium text-muted-foreground">Location verified: Unknown (couldn't check)</span>
              )}
            </div>
            {diag.error && <p className="text-[hsl(var(--danger))]">{diag.error}</p>}
            <div className="flex gap-2 pt-2 flex-wrap">
              {doc.drive_link && (
                <a href={doc.drive_link} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="h-7 text-xs"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open in Drive</Button>
                </a>
              )}
              {diag.location_verified === false && diag.drive_file_id && !diag.drive_trashed && (
                <Button size="sm" className="h-7 text-xs" data-testid="knowledge-diagnostics-move" disabled={moving} onClick={move}>
                  <FolderInput className="h-3.5 w-3.5 mr-1" />{moving ? "Moving…" : "Move to Knowledge folder"}
                </Button>
              )}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const KnowledgeLibraryTab = () => {
  const [folder, setFolder] = useState(null);
  const [folderInput, setFolderInput] = useState("");
  const [editingFolder, setEditingFolder] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [docs, setDocs] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD_FORM);
  const [uploadKind, setUploadKind] = useState("document"); // "document" | "video" | "youtube"
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [diagnosingDoc, setDiagnosingDoc] = useState(null);

  const loadFolder = useCallback(() => api.get("/admin/knowledge/drive-folder").then((r) => setFolder(r.data)).catch(() => setFolder(null)), []);
  const loadDocs = useCallback(() => {
    api.get("/admin/knowledge/documents", { params: { include_archived: showArchived } })
      .then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  }, [showArchived]);
  useEffect(() => {
    loadFolder();
    api.get("/admin/knowledge/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
    api.get("/admin/knowledge/departments").then((r) => setDepartmentOptions(r.data.departments || [])).catch(() => {});
    api.get("/users/roles").then((r) => setRoleOptions(r.data.map((x) => x.role))).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, [loadFolder]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const publishToggle = async (doc) => {
    try {
      await api.post(`/admin/knowledge/documents/${doc.document_id}/${doc.status === "published" ? "unpublish" : "publish"}`);
      toast.success(doc.status === "published" ? "Reverted to Draft — hidden from the Knowledge Base again" : "Published — now visible to employees it applies to");
      loadDocs();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveFolder = async () => {
    if (!folderInput.trim()) return;
    setSavingFolder(true);
    try {
      const { data } = await api.post("/admin/knowledge/drive-folder", { folder: folderInput.trim() });
      toast.success(`Saved — now using "${data.folder_name || data.folder_id}"`);
      setFolderInput(""); setEditingFolder(false); loadFolder();
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingFolder(false); }
  };

  const kindNoun = uploadKind === "document" ? "document" : "video";

  const upload = async () => {
    if (!uploadForm.title.trim()) { toast.warning(`Enter a ${kindNoun} title`); return; }
    if (!uploadForm.category) { toast.warning("Choose a category"); return; }

    /* YouTube: a plain JSON create, no file and no Drive folder needed. The
       URL is validated SERVER-side (routers_knowledge.extract_youtube_id) —
       this only checks non-emptiness, deliberately: duplicating the URL
       grammar here would create a second, drifting source of truth about
       what counts as a valid YouTube link, and a client-side check can be
       bypassed anyway. The server's own rejection message is what the admin
       sees. */
    if (uploadKind === "youtube") {
      if (!youtubeUrl.trim()) { toast.warning("Paste a YouTube link"); return; }
      setUploading(true);
      try {
        const { data: created } = await api.post("/admin/knowledge/youtube", {
          url: youtubeUrl.trim(),
          title: uploadForm.title.trim(),
          category: uploadForm.category,
          product_code: uploadForm.product_code || null,
          department: uploadForm.department.trim() || null,
          description: uploadForm.description.trim(),
        });
        toast.success("YouTube video added — Draft. Publish it to make it visible to employees.", {
          duration: 10000,
          action: { label: "Publish now", onClick: () => publishToggle(created) },
        });
        setUploadForm(EMPTY_UPLOAD_FORM); setYoutubeUrl("");
        loadDocs();
      } catch (e) { toast.error(errDetail(e)); } finally { setUploading(false); }
      return;
    }

    if (!file) { toast.warning(`Choose a ${uploadKind === "video" ? "video" : "file"} to upload`); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", uploadForm.title.trim());
      fd.append("category", uploadForm.category);
      if (uploadForm.product_code) fd.append("product_code", uploadForm.product_code);
      if (uploadForm.department.trim()) fd.append("department", uploadForm.department.trim());
      if (uploadForm.description.trim()) fd.append("description", uploadForm.description.trim());
      const endpoint = uploadKind === "video" ? "/admin/knowledge/videos" : "/admin/knowledge/documents";
      const { data: created } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded successfully — Draft. Publish this ${uploadKind === "video" ? "video" : "document"} to make it visible to employees.`, {
        duration: 10000,
        action: { label: "Publish now", onClick: () => publishToggle(created) },
      });
      setUploadForm(EMPTY_UPLOAD_FORM); setFile(null);
      const fileInput = document.getElementById("knowledge-upload-file-input");
      if (fileInput) fileInput.value = "";
      loadDocs();
    } catch (e) { toast.error(errDetail(e)); } finally { setUploading(false); }
  };

  const setArchived = async (doc, active) => {
    try {
      await api.post(`/admin/knowledge/documents/${doc.document_id}/archive`, { active });
      toast.success(active ? "Document restored" : "Document archived (Drive file untouched)");
      loadDocs();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!folder) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-4" data-testid="knowledge-library-admin">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Google Drive Knowledge Library Folder</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-muted-foreground">Where uploaded SOPs/policies/frameworks/training/sales scripts/compliance documents physically live. Reuses SalesOS's existing Google connection — no separate account or sign-in.</p>
          {folder.folder_id && !editingFolder ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs flex-wrap">
                <FolderCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                <span className="font-medium">{folder.folder_name || "(name unavailable — link/ID still work)"}</span>
                {folder.connection_status !== "connected" && (
                  <Badge variant="outline" className="text-[9px] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">Google not connected</Badge>
                )}
              </div>
              <p className="text-[10px] font-mono text-muted-foreground truncate">{folder.folder_id}</p>
              {folder.last_validated_at && (
                <p className="text-[10px] text-muted-foreground">
                  Last validated {fmtIST(folder.last_validated_at)}
                  {folder.last_validation_error && <span className="text-[hsl(var(--danger))]"> — {folder.last_validation_error}</span>}
                </p>
              )}
              <div className="flex gap-2">
                <a href={folder.folder_link} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="knowledge-folder-open">
                    <ExternalLink className="h-3 w-3 mr-1" /> Open folder
                  </Button>
                </a>
                <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="knowledge-folder-change" onClick={() => setEditingFolder(true)}>Change folder</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input data-testid="knowledge-folder-input" placeholder="Paste a Google Drive folder link or ID…" className="h-9 text-xs" value={folderInput} onChange={(e) => setFolderInput(e.target.value)} />
              <Button data-testid="knowledge-folder-save" size="sm" disabled={savingFolder || !folderInput.trim()} onClick={saveFolder}>{savingFolder ? "Checking…" : "Save"}</Button>
              {folder.folder_id && <Button size="sm" variant="ghost" onClick={() => { setEditingFolder(false); setFolderInput(""); }}>Cancel</Button>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-heading">
            {uploadKind === "document" ? "Upload document"
              : uploadKind === "video" ? "Upload training video"
                : "Add YouTube video"}
          </CardTitle>
          <div className="flex gap-1 rounded-md border p-0.5">
            <Button type="button" size="sm" variant={uploadKind === "document" ? "default" : "ghost"} className="h-6 text-xs px-2"
              data-testid="knowledge-upload-kind-document" onClick={() => { setUploadKind("document"); setFile(null); }}>
              Document
            </Button>
            <Button type="button" size="sm" variant={uploadKind === "video" ? "default" : "ghost"} className="h-6 text-xs px-2"
              data-testid="knowledge-upload-kind-video" onClick={() => { setUploadKind("video"); setFile(null); }}>
              Upload video
            </Button>
            <Button type="button" size="sm" variant={uploadKind === "youtube" ? "default" : "ghost"} className="h-6 text-xs px-2"
              data-testid="knowledge-upload-kind-youtube" onClick={() => { setUploadKind("youtube"); setFile(null); }}>
              <Youtube className="h-3 w-3 mr-1" />YouTube link
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{uploadKind === "document" ? "Document title *" : "Video title *"}</Label>
              <Input data-testid="knowledge-upload-title" value={uploadForm.title}
                     onChange={(e) => setUploadForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Consultation Outcome SOP" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category *</Label>
              <Select value={uploadForm.category} onValueChange={(v) => setUploadForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="knowledge-upload-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Product (optional)</Label>
              <Select value={uploadForm.product_code} onValueChange={(v) => setUploadForm((f) => ({ ...f, product_code: v }))}>
                <SelectTrigger data-testid="knowledge-upload-product"><SelectValue placeholder="Any product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Department (optional)</Label>
              <Input data-testid="knowledge-upload-department" value={uploadForm.department}
                     onChange={(e) => setUploadForm((f) => ({ ...f, department: e.target.value }))} placeholder="e.g. Sales" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description / notes (optional)</Label>
            <Textarea data-testid="knowledge-upload-description" rows={2} value={uploadForm.description}
                      onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          {uploadKind === "youtube" ? (
            <div className="space-y-1">
              <Label className="text-xs">YouTube link *</Label>
              <Input data-testid="knowledge-upload-youtube-url" className="text-xs"
                     placeholder="https://www.youtube.com/watch?v=…"
                     value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} />
              <p className="text-[10px] text-muted-foreground">
                Accepts youtube.com/watch?v=…, youtu.be/… or youtube.com/embed/… — a single video,
                not a playlist or channel. Nothing is uploaded: SalesOS stores only the video ID and
                embeds it, so no Drive folder is needed for this.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="text-xs">
                {uploadKind === "video"
                  ? "Video file * (MP4, WebM — max 250 MB)"
                  : "File * (PDF, DOC/DOCX, TXT, XLS/XLSX, PPT/PPTX, CSV, MD — max 25 MB)"}
              </Label>
              <input id="knowledge-upload-file-input" data-testid="knowledge-upload-file" type="file"
                     accept={uploadKind === "video" ? "video/mp4,video/webm,.mp4,.webm" : undefined}
                     className="text-xs"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          )}
          <Button size="sm" disabled={uploading} onClick={upload} data-testid="knowledge-upload-submit">
            {uploadKind === "youtube"
              ? <Link2 className="h-3.5 w-3.5 mr-1.5" />
              : <UploadCloud className="h-3.5 w-3.5 mr-1.5" />}
            {uploading
              ? (uploadKind === "youtube" ? "Adding…" : "Uploading…")
              : uploadKind === "youtube" ? "Add YouTube video"
                : uploadKind === "video" ? "Upload video to Drive" : "Upload to Drive"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-heading">Documents</CardTitle>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>Category</TableHead>
                <TableHead>Product</TableHead><TableHead>Department</TableHead><TableHead>Visibility</TableHead>
                <TableHead>Uploaded</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {docs && docs.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground text-sm py-6">No documents yet</TableCell></TableRow>
                )}
                {docs && docs.map((d) => {
                  const meta = CONTENT_TYPE_META[d.content_type] || CONTENT_TYPE_META.document;
                  const TypeIcon = meta.icon;
                  const isYouTube = d.content_type === "youtube";
                  return (
                  <TableRow key={d.document_id} data-testid={`knowledge-doc-row-${d.document_id}`}>
                    <TableCell className="text-xs">
                      <div className="font-medium flex items-center gap-1.5">
                        <TypeIcon className="h-3 w-3 text-muted-foreground shrink-0"
                          aria-label={meta.label} data-testid={`knowledge-doc-type-${d.document_id}`} />
                        {d.title}
                      </div>
                      {d.description && <div className="text-muted-foreground truncate max-w-xs">{d.description}</div>}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className={`border-0 capitalize text-[10px] ${STATUS_TONE[docStatusLabel(d)] || STATUS_TONE.draft}`} data-testid={`knowledge-doc-status-${d.document_id}`}>
                        {docStatusLabel(d)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{d.category}</TableCell>
                    <TableCell className="text-xs">{d.product_code || "—"}</TableCell>
                    <TableCell className="text-xs">{d.department || "—"}</TableCell>
                    <TableCell className="text-xs">{VISIBILITY_LABEL[d.visibility?.type] || "All employees"}</TableCell>
                    <TableCell className="text-xs">{fmtIST(d.uploaded_at)} · {d.uploaded_by_name || d.uploaded_by}</TableCell>
                    <TableCell className="flex gap-1 justify-end">
                      {/* A YouTube item has no Drive file at all, so "Open in
                          Drive", the storage diagnostics and the move-to-folder
                          reconcile are all meaningless for it — it links out to
                          YouTube instead. This is an admin-only outbound link
                          built from the stored, already-validated video ID; it
                          is never an embed. */}
                      {isYouTube ? (
                        d.youtube_video_id && (
                          <a href={`https://www.youtube.com/watch?v=${d.youtube_video_id}`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-open-${d.document_id}`} title="Open on YouTube">
                              <Youtube className="h-3.5 w-3.5" />
                            </Button>
                          </a>
                        )
                      ) : (
                        <>
                          <a href={d.drive_link} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-open-${d.document_id}`} title="Open in Drive">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </a>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-diagnostics-${d.document_id}`} title="Drive storage diagnostics" onClick={() => setDiagnosingDoc(d)}>
                            <MapPin className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {!d.deleted && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-edit-${d.document_id}`} title="Edit metadata / visibility" onClick={() => setEditingDoc(d)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!d.deleted && d.active && d.status !== "archived" && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-${d.status === "published" ? "unpublish" : "publish"}-${d.document_id}`}
                          title={d.status === "published" ? "Revert to Draft" : "Publish"} onClick={() => publishToggle(d)}>
                          {d.status === "published" ? <Undo2 className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                      {!d.deleted && (d.active ? (
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-archive-${d.document_id}`} title="Archive" onClick={() => setArchived(d, false)}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`knowledge-doc-restore-${d.document_id}`} title="Restore" onClick={() => setArchived(d, true)}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      ))}
                      {!d.deleted && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" data-testid={`knowledge-doc-delete-${d.document_id}`} title={isYouTube ? "Delete (removes the link; the YouTube video itself is untouched)" : "Delete (moves Drive file to Trash)"} onClick={() => setDeletingDoc(d)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditDocDialog
        doc={editingDoc}
        categories={categories}
        products={products}
        departmentOptions={departmentOptions}
        roleOptions={roleOptions}
        onClose={() => setEditingDoc(null)}
        onSaved={() => { setEditingDoc(null); loadDocs(); }}
      />
      <DeleteDocDialog
        doc={deletingDoc}
        onClose={() => setDeletingDoc(null)}
        onDeleted={() => { setDeletingDoc(null); loadDocs(); }}
      />
      <DiagnosticsDialog
        doc={diagnosingDoc}
        onClose={() => setDiagnosingDoc(null)}
        onReconciled={loadDocs}
      />
    </div>
  );
};
